import { useEffect, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { colord } from "colord";

import { misc } from "app/themes/colors";
import CircularProgress from "components/circular-progress";
import { PatientNote } from "services/types/patient-notes.type";
import type { Patient } from "services/types/patients.type";

import { DRAWER_WIDTH } from "../patient-drawer";
import NotePreview from "../patient-header/header-notes/note-preview";
import PatientNotesForm, {
  PatientNotesFormValues
} from "./components/patient-notes-form.component";
import { usePatientNotesDrawer } from "./use-patient-notes-drawer.hook";

const getInitialValues = (
  editMode: PatientNote | undefined,
  patient: Patient | undefined
): PatientNotesFormValues => {
  if (editMode) {
    return {
      id: editMode?.id,
      title: editMode?.title ?? "",
      description: editMode?.description ?? "",
      patient: patient
        ? {
            label: String(patient.fullName),
            value: String(patient.id)
          }
        : null
    };
  }

  return {
    title: "",
    description: "",
    patient: patient
      ? { label: String(patient.fullName), value: String(patient.id) }
      : null
  };
};

export default function PatientNotesDrawer() {
  const theme = useTheme();
  const {
    isOpen,
    isFetching,
    notes,
    handleClose,
    isOpenCreate,
    handleDelete,
    editMode,
    patient,
    handleOpenCreate,
    handleEdit
  } = usePatientNotesDrawer();

  const initialValues = useMemo(
    () => getInitialValues(editMode, patient),
    [editMode, patient]
  );

  // A `persistent` Drawer renders no Modal, so MUI never calls `onClose` for
  // Escape — the prop below is inert on its own. Listen on the window instead.
  // Bubble phase on purpose: a Dialog stacked above this drawer (the delete
  // confirm, the launcher) stops propagation of the Escape it consumes, so one
  // keypress closes the topmost layer only. Escape inside TinyMCE's iframe
  // never reaches the parent window at all, which is the desired outcome too.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleClose]);

  return (
    <Drawer
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH
        }
      }}
      variant="persistent"
      anchor="right"
      sx={{
        zIndex: theme.zIndex.snackbar
      }}
      open={isOpen}
      onClose={handleClose}
    >
      {isFetching && <CircularProgress sx={{ width: "100%" }} />}
      {!isFetching && notes && (
        <>
          <Box
            display="flex"
            justifyContent="end"
            position="absolute"
            width="100%"
          >
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box pl={3} pt={2} pr={0}>
            {!isOpenCreate && (
              <Stack
                justifyContent="center"
                sx={{ width: "100%", height: "100%", ml: -1 }}
                spacing={1}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    mb: 2,
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <Typography
                    variant="h2"
                    color="textSecondary"
                    sx={{
                      fontSize: "28px",
                      color: colord(misc.darkYellow).darken(0.25).toHex()
                    }}
                  >
                    Notes
                  </Typography>
                  <Button
                    onClick={() => handleOpenCreate(patient?.id)}
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{
                      height: 28,

                      "&.MuiButton-outlined": {
                        color: colord(misc.darkYellow).darken(0.25).toHex(),
                        borderColor: misc.darkYellow
                      },
                      "&.MuiButton-outlined:hover": {
                        color: colord(misc.darkYellow).darken(0.35).toHex(),
                        borderColor: colord(misc.darkYellow)
                          .darken(0.125)
                          .toHex()
                      }
                    }}
                  >
                    Create
                  </Button>
                </Stack>

                {notes.map((note: PatientNote) => (
                  <NotePreview
                    key={note.id}
                    note={note}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                  />
                ))}
              </Stack>
            )}

            {isOpenCreate && <PatientNotesForm initialValues={initialValues} />}
          </Box>
        </>
      )}
    </Drawer>
  );
}
